import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart3, Download, TrendingUp, Wallet, Receipt, Ban } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { downloadXlsx, fmtDate, todayStamp } from "@/lib/csv";

const monthsFr = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

function fmtEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));
}

function KPI({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="p-4 border-l-4 border-l-klein rounded-sm" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm bg-klein/10 text-klein flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="font-display font-black text-2xl tabular-nums leading-tight">{value}</div>
          {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

export default function BilanVentes() {
  const navigate = useNavigate();
  const now = new Date();
  const [period, setPeriod] = useState("month"); // month | year | all
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearsList = useMemo(() => {
    const out = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y -= 1) out.push(y);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === "month") { params.year = year; params.month = month; }
      if (period === "year") { params.year = year; }
      const { data: res } = await api.get("/reports/sales", { params });
      setData(res);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Accès réservé aux administrateurs");
        navigate("/", { replace: true });
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [period, year, month]);

  const exportExcel = () => {
    if (!data?.ventes?.length) {
      toast.error("Aucune vente à exporter");
      return;
    }
    const periodLabel = period === "month"
      ? `${monthsFr[month - 1]}-${year}`
      : period === "year" ? `${year}` : "tout";
    downloadXlsx(
      `bilan-ventes-${periodLabel}-${todayStamp()}`,
      [
        { key: "date_vente", label: "Date", format: (v) => fmtDate(v) },
        { key: "numero_vo", label: "N° VO" },
        { key: "plaque", label: "Plaque" },
        { key: "marque", label: "Marque" },
        { key: "modele", label: "Modèle" },
        { key: "annee", label: "Année" },
        { key: "client_nom", label: "Client" },
        { key: "prix_vente", label: "Prix HT (€)", format: (v) => Number(v || 0).toFixed(2) },
        { key: "cout_interventions", label: "Atelier (€)", format: (v) => Number(v || 0).toFixed(2) },
        { key: "total", label: "Total (€)", format: (v) => Number(v || 0).toFixed(2) },
        { key: "paye", label: "Encaissé (€)", format: (v) => Number(v || 0).toFixed(2) },
        { key: "restant", label: "Restant dû (€)", format: (v) => Number(v || 0).toFixed(2) },
      ],
      data.ventes,
      "Bilan ventes",
    );
    toast.success(`${data.ventes.length} ventes exportées`);
  };

  const chartData = useMemo(() => {
    if (!data?.by_month) return [];
    return data.by_month.map((m) => {
      const [yy, mm] = m.period.split("-");
      return {
        label: `${monthsFr[Number(mm) - 1].slice(0, 3)} ${yy.slice(2)}`,
        ca: Math.round(m.ca),
        count: m.count,
      };
    });
  }, [data]);

  return (
    <div className="space-y-6" data-testid="bilan-ventes-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground flex items-center gap-2">
            <BarChart3 size={14} /> Bilan ventes · Admin
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">
            Bilan des ventes
          </h1>
          <p className="text-sm text-muted-foreground">
            Suivi du chiffre d'affaires et des ventes par période.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={exportExcel}
          disabled={!data?.ventes?.length}
          className="rounded-sm h-11"
          data-testid="bilan-export-excel"
        >
          <Download size={14} className="mr-1.5" /> Exporter Excel
        </Button>
      </header>

      {/* Filtres */}
      <Card className="p-4 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Période</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="bilan-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Par mois</SelectItem>
                <SelectItem value="year">Par année</SelectItem>
                <SelectItem value="all">Tout l'historique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {period !== "all" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Année</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bilan-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearsList.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {period === "month" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Mois</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="bilan-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthsFr.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </Card>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPI
          icon={Wallet}
          label="Chiffre d'affaires"
          value={fmtEUR(data?.totals?.ca || 0)}
          sub={period === "month" ? `${monthsFr[month - 1]} ${year}` : period === "year" ? `${year}` : "Toutes périodes"}
          testId="bilan-kpi-ca"
        />
        <KPI
          icon={Receipt}
          label="Nb de ventes"
          value={data?.totals?.count || 0}
          sub="véhicules vendus"
          testId="bilan-kpi-count"
        />
        <KPI
          icon={TrendingUp}
          label="Panier moyen"
          value={fmtEUR(data?.totals?.panier_moyen || 0)}
          sub="par véhicule"
          testId="bilan-kpi-avg"
        />
      </div>

      {/* Graphique */}
      <Card className="p-4 rounded-sm" data-testid="bilan-chart">
        <div className="text-sm font-bold mb-3">Évolution mensuelle · Chiffre d'affaires</div>
        {chartData.length === 0 ? (
          <div className="text-sm text-muted-foreground py-12 text-center">Aucune donnée pour cette période</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" fontSize={11} stroke="#64748b" />
                <YAxis fontSize={11} stroke="#64748b" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip
                  formatter={(value, name) => name === "ca" ? [fmtEUR(value), "CA"] : [value, "Ventes"]}
                  contentStyle={{ borderRadius: 2, fontSize: 12 }}
                />
                <Bar dataKey="ca" fill="#002FA7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Ventes annulées de la période */}
      {data?.cancelled?.count > 0 && (
        <Card className="p-4 rounded-sm border-l-4 border-l-rose-500" data-testid="bilan-cancelled">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold flex items-center gap-2">
              <Ban size={14} className="text-rose-600" />
              Ventes annulées ({data.cancelled.count})
            </div>
            <div className="text-xs text-muted-foreground">
              Total annulé : <span className="font-bold text-rose-700">{fmtEUR(data.cancelled.total)}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Date vente</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">N° VO</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Plaque</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Véhicule</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Client</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Motif</th>
                  <th className="text-right py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Montant</th>
                </tr>
              </thead>
              <tbody>
                {data.cancelled.ventes.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/ventes/${v.id}`)}
                    className="border-b last:border-b-0 hover:bg-rose-50 cursor-pointer transition-colors"
                    data-testid={`bilan-cancelled-row-${v.id}`}
                  >
                    <td className="py-2">{fmtDate(v.date_vente)}</td>
                    <td className="py-2 font-mono text-rose-600 font-bold">{v.numero_vo || "—"}</td>
                    <td className="py-2 font-mono font-bold">{v.plaque}</td>
                    <td className="py-2">{v.marque} {v.modele}</td>
                    <td className="py-2">{v.client_nom || "—"}</td>
                    <td className="py-2 text-muted-foreground italic text-xs">{v.cancel_reason || "—"}</td>
                    <td className="py-2 text-right tabular-nums font-bold text-rose-700 line-through decoration-2">
                      {fmtEUR(v.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Liste détaillée */}
      <Card className="p-4 rounded-sm" data-testid="bilan-ventes-list">
        <div className="text-sm font-bold mb-3">Détail des ventes ({data?.ventes?.length || 0})</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Chargement…</div>
        ) : !data?.ventes?.length ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Aucune vente</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">N° VO</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Plaque</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Véhicule</th>
                  <th className="text-left py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Client</th>
                  <th className="text-right py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Total</th>
                  <th className="text-right py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Encaissé</th>
                  <th className="text-right py-2 text-[11px] uppercase tracking-wider text-muted-foreground">Restant</th>
                </tr>
              </thead>
              <tbody>
                {data.ventes.map((v) => (
                  <tr
                    key={v.id}
                    onClick={() => navigate(`/ventes/${v.id}`)}
                    className="border-b last:border-b-0 hover:bg-klein/5 cursor-pointer transition-colors"
                    data-testid={`bilan-vente-row-${v.id}`}
                    title="Cliquer pour ouvrir la fiche véhicule"
                  >
                    <td className="py-2">{fmtDate(v.date_vente)}</td>
                    <td className="py-2 font-mono text-klein font-bold">{v.numero_vo || "—"}</td>
                    <td className="py-2 font-mono font-bold">{v.plaque}</td>
                    <td className="py-2">{v.marque} {v.modele}{v.annee ? ` (${v.annee})` : ""}</td>
                    <td className="py-2">{v.client_nom || "—"}</td>
                    <td className="py-2 text-right tabular-nums font-bold">{fmtEUR(v.total)}</td>
                    <td className="py-2 text-right tabular-nums text-emerald-700">{fmtEUR(v.paye)}</td>
                    <td className={`py-2 text-right tabular-nums ${v.restant > 0 ? "text-rose-700 font-bold" : "text-muted-foreground"}`}>
                      {fmtEUR(v.restant)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
